import type { IssueCase, IssueDetail, IssueNextAction } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { backlogUrl } from '../../lib/backlog-url.js';
import {
  formatIssueTime,
  formatShortDate,
  ISSUE_REJECT_REASON_LABEL_KEYS,
  ISSUE_VERDICT_LABEL_KEYS,
  jstToday,
} from '../../lib/issue-mgmt-format.js';
import {
  CASE_ATTENTION_REASON_KEYS,
  MISSING_FIELD_LABEL_KEYS,
} from '../../lib/issue-mgmt-next-action.js';
import type { Translate } from '../../lib/issue-mgmt-values.js';
import type { DecisionChoice } from './DecideDialog.js';
import { IssueText } from './IssueText.js';

/**
 * 操作の段（下の灰色の段）。
 * @graph-connects none
 */
export function Actions({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 py-4">
      {children}
    </div>
  );
}

/**
 * 判定のボタン（対応する・保留・対応しないの小窓を開く。再浮上なら「このままにする」も）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定のカードに、対応する・保留・対応しないの3つと、再浮上した課題なら判定をこのままにするボタンを出す
 * @graph-connects none
 */
export function JudgeButtons({
  onDecide,
  keep,
}: {
  onDecide: (decision: DecisionChoice) => void;
  /** 再浮上の「このままにする」（出さないなら null） */
  keep: { loading: boolean; onKeep: () => void } | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        size="lg"
        onClick={() => {
          onDecide('accepted');
        }}
      >
        {t('issueMgmt.verdict.accepted')}
      </Button>
      <Button
        size="lg"
        variant="secondary"
        onClick={() => {
          onDecide('on_hold');
        }}
      >
        {t('issueMgmt.verdict.onHold')}
      </Button>
      <Button
        size="lg"
        variant="secondary"
        onClick={() => {
          onDecide('rejected');
        }}
      >
        {t('issueMgmt.verdict.rejected')}
      </Button>
      {keep !== null && (
        <Button size="lg" variant="ghost" loading={keep.loading} onClick={keep.onKeep}>
          {t('issueMgmt.card.keep')}
        </Button>
      )}
    </div>
  );
}

/**
 * カードの区画（小さい見出しと中身）。
 * @graph-connects none
 */
export function CardSection({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="px-5 py-3">
      <p className="mb-1 flex items-center gap-2 text-xs font-semibold text-[var(--color-text-muted)]">
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * 「何が起きているか」（AI下書きなら札）。空なら「まだ書かれていません」。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定・確認のカードに、課題の中身の1項目目（何が起きているか）を AI下書きの札つきで出す
 * @graph-connects IssueText [calls] 伏せ字の札
 */
export function WhatSection({ issue }: { issue: IssueDetail }) {
  const { t } = useTranslation();
  const what = issue.content.what;
  return (
    <CardSection
      label={
        <>
          {t('issueMgmt.content.what')}
          {what.mark === 'ai_draft' && <Badge size="sm">{t('issueMgmt.content.aiDraft')}</Badge>}
        </>
      }
    >
      <p
        className={`text-sm ${what.text === null ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text-primary)]'}`}
      >
        {what.text === null ? t('issueMgmt.content.empty') : <IssueText text={what.text} />}
      </p>
    </CardSection>
  );
}

/**
 * 声1件の1行（日時・チャンネル・非公開の印・要点・Slackで開く）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business カードに声1件を、日時・チャンネル・非公開の印・要点と Slack へのリンクで1行に出す
 * @graph-connects formatIssueTime [calls] 投稿日時
 */
export function VoiceLine({ voice, now }: { voice: IssueDetail['voices'][number]; now: Date }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1 text-sm">
      <span className="text-xs text-[var(--color-text-muted)]">
        {formatIssueTime(voice.postedAt, voice.postedDate, now) ?? t('issueMgmt.common.dash')}{' '}
        {voice.channelName === null ? '' : `#${voice.channelName}`}
      </span>
      {voice.isPrivate && (
        <span title={t('issueMgmt.common.privateHint')}>
          <Badge size="sm">{t('issueMgmt.common.private')}</Badge>
        </span>
      )}
      <span className="min-w-0 flex-1 text-[var(--color-text-primary)]">
        {voice.bodyDeleted ? t('issueMgmt.voices.deleted') : <IssueText text={voice.pointTitle} />}
      </span>
      {voice.slackUrl !== null && (
        <a
          href={voice.slackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[var(--color-accent-primary)] hover:underline"
        >
          {t('issueMgmt.common.openSlack')}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      )}
    </li>
  );
}

/**
 * 判定のカードの声（件数と新しい2件・課題の詳細へのリンク）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定のカードに、課題の声の件数と新しい2件を出し、課題の詳細へ移れるようにする
 * @graph-connects VoiceLine [calls] 声1件
 */
export function RecentVoices({ issue, now }: { issue: IssueDetail; now: Date }) {
  const { t } = useTranslation();
  return (
    <CardSection label={t('issueMgmt.card.recentVoices', { n: String(issue.voiceCount) })}>
      {issue.voices.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('issueMgmt.voices.empty')}</p>
      ) : (
        <ul>
          {issue.voices.slice(0, 2).map((voice) => (
            <VoiceLine key={voice.pointId} voice={voice} now={now} />
          ))}
        </ul>
      )}
      <Link
        to="/issue-mgmt/issues/$issueId"
        params={{ issueId: issue.id }}
        className="mt-1 inline-block text-xs text-[var(--color-accent-primary)] hover:underline"
      >
        {t('issueMgmt.card.openDetail')}
      </Link>
    </CardSection>
  );
}

/**
 * 判定の文言（判定の名前と、対応しないなら理由・保留なら見直す日）。
 * @graph-connects none
 */
function verdictText(issue: IssueDetail, t: Translate, now: Date): string {
  const name = t(ISSUE_VERDICT_LABEL_KEYS[issue.verdict]);
  if (issue.verdict === 'rejected' && issue.rejectReason !== null) {
    return t('issueMgmt.card.verdictWithReason', {
      verdict: name,
      reason: t(ISSUE_REJECT_REASON_LABEL_KEYS[issue.rejectReason]),
    });
  }
  if (issue.verdict === 'on_hold' && issue.holdUntil !== null) {
    return t('issueMgmt.card.verdictWithReason', {
      verdict: name,
      reason: formatShortDate(issue.holdUntil, now),
    });
  }
  return name;
}

/**
 * 判定し直すカードの黄色の帯（見直す日が来た・新しい声が来た・判定者以外が判定を変えた）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定し直すカードに、見直す日の到来・判定の後の新しい声・判定者以外の判定の変更を、前の判定とともに知らせる
 * @graph-connects verdictText [calls] 判定の文言
 */
export function RedecideBanner({
  issue,
  next,
  now,
}: {
  issue: IssueDetail;
  next: Extract<IssueNextAction, { kind: 'redecide' }>;
  now: Date;
}) {
  const { t } = useTranslation();
  const current = verdictText(issue, t, now);
  const message = (() => {
    switch (next.reason) {
      case 'hold_date':
        return t('issueMgmt.card.redecideHold', {
          date:
            issue.holdUntil === null
              ? t('issueMgmt.common.dash')
              : formatShortDate(issue.holdUntil, now),
          verdict: current,
        });
      case 'new_voices':
        return t('issueMgmt.card.redecideVoices', {
          n: String(next.newVoiceCount),
          verdict: current,
        });
      case 'changed':
        return t('issueMgmt.card.redecideChanged', {
          name: next.changedBy ?? t('issueMgmt.error.someone'),
          before:
            next.previousVerdict === null
              ? t('issueMgmt.common.dash')
              : t(ISSUE_VERDICT_LABEL_KEYS[next.previousVerdict]),
          after: current,
        });
      default: {
        const unreachable: never = next.reason;
        throw new Error(`unknown reason: ${String(unreachable)}`);
      }
    }
  })();
  return (
    <div className="mx-5 mt-3 flex items-start gap-2 rounded-lg bg-[color-mix(in_srgb,var(--color-warning)_14%,transparent)] px-3 py-2 text-sm text-[var(--color-text-primary)]">
      <AlertTriangle
        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning)]"
        aria-hidden="true"
      />
      <span>{message}</span>
    </div>
  );
}

/**
 * 案件を確かめるカードの中身（キー・題名・何がおかしいかの1文・Backlogで開く）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件を確かめるカードに、案件のキー・題名と、見つからない・保留中・期限超過など何がおかしいかを1文で出す
 * @graph-connects backlogUrl [calls] Backlog へのリンク
 */
export function CaseCheckSection({
  next,
  caseRow,
  now,
}: {
  next: Extract<IssueNextAction, { kind: 'check_case' }>;
  caseRow: IssueCase | undefined;
  now: Date;
}) {
  const { t } = useTranslation();
  return (
    <CardSection label={t('issueMgmt.card.case')}>
      <p className="text-sm text-[var(--color-text-primary)]">
        <span className="font-semibold">{next.caseKey}</span>
        {caseRow !== undefined && caseRow.title !== null && ` ${caseRow.title}`}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        {t(CASE_ATTENTION_REASON_KEYS[next.attention], {
          status: caseRow?.statusName ?? t('issueMgmt.common.dash'),
          date:
            caseRow?.dueDate === undefined || caseRow.dueDate === null
              ? t('issueMgmt.common.dash')
              : formatShortDate(caseRow.dueDate, now),
        })}
      </p>
      <a
        href={backlogUrl(next.caseKey)}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--color-accent-primary)] hover:underline"
      >
        {t('issueMgmt.card.openBacklog')}
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    </CardSection>
  );
}

/**
 * 直ったかのカードの中身（リリースされた案件、または何をしたか・直った状態・リリース後に来た声の数）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 直ったかのカードに、リリースされた案件（開発しない課題なら何をしたか）と直った状態、リリース後に来た声の数を出す
 * @graph-connects formatShortDate [calls] リリース日
 */
export function VerifySection({
  issue,
  cases,
  now,
}: {
  issue: IssueDetail;
  cases: readonly IssueCase[];
  now: Date;
}) {
  const { t } = useTranslation();
  const released = cases.filter((c) => c.stage === 'done' && !c.deleted);
  const releaseDates = released.flatMap((c) => (c.releasedDate === null ? [] : [c.releasedDate]));
  const lastRelease =
    releaseDates.length === 0 ? null : (releaseDates.sort()[releaseDates.length - 1] ?? null);
  const voicesAfter =
    lastRelease === null
      ? null
      : issue.voices.filter((v) => {
          const date = v.postedAt === null ? v.postedDate : jstToday(new Date(v.postedAt));
          return date !== null && date > lastRelease;
        }).length;
  return (
    <>
      {issue.devRequired === false ? (
        <CardSection label={t('issueMgmt.card.doneNote')}>
          <p className="text-sm text-[var(--color-text-primary)]">
            {issue.doneNote ?? t('issueMgmt.content.empty')}
          </p>
        </CardSection>
      ) : (
        <CardSection label={t('issueMgmt.card.released')}>
          <ul className="text-sm text-[var(--color-text-primary)]">
            {released.map((c) => (
              <li key={c.caseKey}>
                <span className="font-semibold">{c.caseKey}</span>
                {c.title !== null && ` ${c.title}`}
                {c.releasedDate !== null &&
                  `（${t('issueMgmt.card.releasedOn', { date: formatShortDate(c.releasedDate, now) })}）`}
              </li>
            ))}
          </ul>
        </CardSection>
      )}
      <CardSection label={t('issueMgmt.content.fixed')}>
        <p
          className={`text-sm ${issue.content.fixed.text === null ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text-primary)]'}`}
        >
          {issue.content.fixed.text ?? t('issueMgmt.content.empty')}
        </p>
        {voicesAfter !== null && (
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t('issueMgmt.card.voicesAfterRelease', { n: String(voicesAfter) })}
          </p>
        )}
      </CardSection>
    </>
  );
}

/**
 * 必須項目のカードの「決まっていない項目」。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 必須項目のカードに、担当・優先度・開発要否のうち決まっていない項目を並べて出す
 * @graph-connects MISSING_FIELD_LABEL_KEYS [references] 項目の名前
 */
export function MissingSection({
  missing,
}: {
  missing: Extract<IssueNextAction, { kind: 'fill' }>['missing'];
}) {
  const { t } = useTranslation();
  return (
    <CardSection label={t('issueMgmt.card.missing')}>
      <p className="text-sm text-[var(--color-text-primary)]">
        {missing.map((field) => t(MISSING_FIELD_LABEL_KEYS[field])).join('・')}
      </p>
    </CardSection>
  );
}

/**
 * 開発しない課題のカードの添え書き（いつ開発しないで対応すると決めたか）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 開発しない課題の対応を確かめるカードに、開発しないで対応すると決めた日を添える
 * @graph-connects formatShortDate [calls] 決めた日
 */
export function StartNote({ issue, now }: { issue: IssueDetail; now: Date }) {
  const { t } = useTranslation();
  return (
    <p className="px-5 pb-3 text-xs text-[var(--color-text-muted)]">
      {t('issueMgmt.card.startNote', {
        date:
          issue.decidedAt === null
            ? t('issueMgmt.common.dash')
            : formatShortDate(jstToday(new Date(issue.decidedAt)), now),
      })}
    </p>
  );
}
